// Bank Logo Utilities - Helper functions for bank logo operations
import { bankLogoService, BankLogo } from '../services/bankLogoService';
import { getBankNameByCode, getBankLogoPathByCode } from './bankCodeMappings';
import { getBankSvgPath, hasBankSvgLogo, getAllBankSvgLogos } from './bankSvgMappings';

/**
 * ==========================================
 * SIMPLIFIED BANK LOGO API (NEW)
 * ==========================================
 * These functions provide immediate, synchronous access to local SVG logos
 * without external dependencies or network requests.
 */

/**
 * Normalize bank name for file matching
 * Handles Unicode characters, accents, and special characters to match SVG file names
 *
 * Examples:
 * - "Itaú" → "itau" (removes accent, converts to lowercase, replaces spaces)
 * - "Banco do Brasil" → "banco-do-brasil"
 * - "Sicoob" → "sicoob"
 * - "Ágios" → "agios"
 *
 * @param bankName - The bank name to normalize
 * @returns Normalized name suitable for file matching
 */
function normalizeBankNameForFile(bankName: string): string {
  if (!bankName) return '';

  return normalizeBankName(bankName)
    .normalize('NFD')                    // Decompose Unicode characters (Itaú → Itaú)
    .replace(/[\u0300-\u036f]/g, '')    // Remove diacritics/accents (Itaú → Itau)
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]/g, '');            // Remove special characters except hyphens
}

/**
 * Get bank logo SVG path - simplified version
 * @param bankName - The bank name (e.g., "Banco do Brasil", "Nubank")
 * @returns The path to the SVG file or empty string if not found
 *
 * @example
 * ```typescript
 * getBankLogoSvgPath("Banco do Brasil") // "/logos/banks-svg/banco-do-brasil.svg"
 * getBankLogoSvgPath("Nubank") // "/logos/banks-svg/nubank.svg"
 * getBankLogoSvgPath("Unknown Bank") // ""
 * ```
 */
export function getBankLogoSvgPath(bankName: string): string {
  if (!bankName) return '';

  // Normalize the bank name to match file naming convention
  const normalizedName = normalizeBankNameForFile(bankName);
  return getBankSvgPath(normalizedName);
}

/**
 * Check if bank has local SVG logo available
 * @param bankName - The bank name to check
 * @returns True if the bank has a local SVG logo
 *
 * @example
 * ```typescript
 * hasLocalBankLogo("Banco do Brasil") // true
 * hasLocalBankLogo("Unknown Bank") // false
 * ```
 */
export function hasLocalBankLogo(bankName: string): boolean {
  if (!bankName) return false;

  const normalizedName = normalizeBankNameForFile(bankName);
  return hasBankSvgLogo(normalizedName);
}

/**
 * Get bank color for fallback - deterministic based on bank name
 * @param bankName - The bank name
 * @returns A deterministic color for the bank
 *
 * @example
 * ```typescript
 * getBankFallbackColor("Banco do Brasil") // "#005EB8"
 * getBankFallbackColor("Nubank") // "#8A05BE"
 * ```
 */
export function getBankFallbackColor(bankName: string): string {
  if (!bankName) return '#6B7280'; // Default gray

  // Use a deterministic hash of the bank name to select a color
  const colors = [
    '#005EB8', // Banco do Brasil blue
    '#CC092F', // Bradesco red
    '#FF6900', // Itaú orange
    '#EC0000', // Santander red
    '#0C7F3E', // Caixa green
    '#8A05BE', // Nubank purple
    '#FF7A00', // Inter orange
    '#9C1E26', // C6 Bank burgundy
    '#21C25E', // PicPay green
    '#28B0E6', // Neon blue
  ];

  // Simple hash: use the sum of character codes
  const hash = bankName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = hash % colors.length;

  return colors[index];
}

/**
 * Get all available bank names from SVG collection
 * @returns Array of bank names that have local SVG logos
 */
export function getAllLocalBankNames(): string[] {
  // Mapping from normalized file names to display names
  const nameMap: Record<string, string> = {
    'abc-brasil': 'ABC Brasil',
    'ailos': 'Ailos',
    'asaas': 'ASAAS',
    'banco-arbi': 'Banco Arbi',
    'banco-bmp': 'Banco BMP',
    'banco-da-amazonia': 'Banco da Amazônia',
    'banco-do-brasil': 'Banco do Brasil',
    'banco-do-espirito-santo': 'Banco do Espírito Santo',
    'banco-do-nordeste': 'Banco do Nordeste',
    'banco-do-para': 'Banco do Pará',
    'banco-do-sergipe': 'Banco do Sergipe',
    'banco-industrial-do-brasil': 'Banco Industrial do Brasil',
    'banco-paulista': 'Banco Paulista',
    'banco-pine': 'Banco Pine',
    'banco-rendimento': 'Banco Rendimento',
    'banco-sofisa': 'Banco Sofisa',
    'banco-topazio': 'Banco Topázio',
    'banco-votorantim': 'Banco Votorantim',
    'bank-of-america': 'Bank of America',
    'banrisul': 'Banrisul',
    'bees-bank': 'Bees Bank',
    'bmg': 'BMG',
    'bnp-paribas': 'BNP Paribas',
    'bradesco': 'Bradesco',
    'brb': 'BRB',
    'bs2': 'BS2',
    'btg-pactual': 'BTG Pactual',
    'caixa': 'Caixa',
    'capitual': 'Capitual',
    'conta-simples': 'Conta Simples',
    'contbank': 'Contbank',
    'cora': 'Cora',
    'credisis': 'Credisis',
    'cresol': 'Cresol',
    'c6-bank': 'C6 Bank',
    'daycoval': 'Daycoval',
    'duepay': 'Duepay',
    'efi': 'EFI',
    'grafeno': 'Grafeno',
    'ifood-pago': 'IFood Pago',
    'infinitepay': 'Infinitepay',
    'inter': 'Inter',
    'ip4y': 'IP4Y',
    'itau': 'Itaú',
    'iugo': 'Iugo',
    'lets-bank': 'Let\'s Bank',
    'linker': 'Linker',
    'magalupay': 'Magalupay',
    'mercado-pago': 'Mercado Pago',
    'mercantil': 'Mercantil',
    'modobank': 'Modobank',
    'mufg': 'MUFG',
    'multiplo-bank': 'Multiplo Bank',
    'neon': 'Neon',
    'omie-cash': 'Omie Cash',
    'omni': 'Omni',
    'original': 'Banco Original',
    'pagseguro': 'Pagseguro',
    'paycash': 'Paycash',
    'picpay': 'PicPay',
    'pinbank': 'Pinbank',
    'quality-digital-bank': 'Quality Digital Bank',
    'recargapay': 'Recargapay',
    'safra': 'Safra',
    'santander': 'Santander',
    'sicoob': 'Sicoob',
    'sicredi': 'Sicredi',
    'sisprime': 'Sisprime',
    'squid': 'Squid',
    'starbank': 'Starbank',
    'stone': 'Stone',
    'sulcredi': 'Sulcredi',
    'transfera': 'Transfera',
    'tribanco': 'Tribanco',
    'unicred': 'Unicred',
    'uniprime': 'Uniprime',
    'xp-investimentos': 'XP Investimentos',
    'zemo-bank': 'Zemo Bank'
  };

  const svgLogos = getAllBankSvgLogos();

  return svgLogos
    .map(({ name }) => nameMap[name] || name)
    .filter(Boolean)
    .sort();
}

/**
 * Brazilian bank name normalization and mapping
 */
export const BANK_NAME_MAPPINGS: Record<string, string> = {
  // Official name -> Common name mappings
  'Banco do Brasil S.A.': 'Banco do Brasil',
  'Banco Bradesco S.A.': 'Bradesco',
  'Banco Itaú Unibanco S.A.': 'Itaú',
  'Banco Santander (Brasil) S.A.': 'Santander',
  'Caixa Econômica Federal': 'Caixa',
  'Nu Pagamentos S.A.': 'Nubank',
  'Banco Inter S.A.': 'Inter',
  'C6 Bank S.A.': 'C6 Bank',
  'PicPay Serviços S.A.': 'PicPay',
  'Banco Neon S.A.': 'Neon',
  'Banco Original S.A.': 'Original',
  'BTG Pactual S.A.': 'BTG Pactual',

  // Abbreviation mappings
  'BB': 'Banco do Brasil',
  'CEF': 'Caixa',
  'NU': 'Nubank',

  // Variations
  'Banco do Brasil': 'Banco do Brasil',
  'Bradesco': 'Bradesco',
  'Itau': 'Itaú',
  'Itaú': 'Itaú',
  'Santander': 'Santander',
  'Caixa': 'Caixa',
  'Caixa Economica': 'Caixa',
  'NuBank': 'Nubank',
  'Nubank': 'Nubank',
  'Inter': 'Inter',
  'Banco Inter': 'Inter',
  'C6': 'C6 Bank',
  'C6Bank': 'C6 Bank',
  'PicPay': 'PicPay',
  'Neon': 'Neon',
  'Banco Neon': 'Neon',
  'Original': 'Original',
  'BTG': 'BTG Pactual',
  'BTG Pactual': 'BTG Pactual'
};

/**
 * Brazilian bank colors for fallback icons
 */
export const BANK_COLORS: Record<string, string> = {
  'Banco do Brasil': '#005EB8',
  'Bradesco': '#CC092F',
  'Itaú': '#FF6900',
  'Santander': '#EC0000',
  'Caixa': '#0C7F3E',
  'Nubank': '#8A05BE',
  'Inter': '#FF7A00',
  'C6 Bank': '#9C1E26',
  'PicPay': '#21C25E',
  'Neon': '#28B0E6',
  'Original': '#FF6B35',
  'BTG Pactual': '#004A7C',
  'Default': '#6B7280'
};

/**
 * Normalize bank name for consistent matching
 */
export function normalizeBankName(bankName: string): string {
  if (!bankName) return '';

  // Remove extra whitespace and convert to proper case
  const cleanName = bankName.trim().replace(/\s+/g, ' ');

  // Check mappings first
  const mappedName = BANK_NAME_MAPPINGS[cleanName] ||
                    BANK_NAME_MAPPINGS[cleanName.toLowerCase()] ||
                    BANK_NAME_MAPPINGS[cleanName.toUpperCase()];

  if (mappedName) {
    return mappedName;
  }

  // Fallback normalization
  return cleanName;
}

/**
 * Get bank color for fallback UI
 */
export function getBankColor(bankName: string): string {
  const normalizedName = normalizeBankName(bankName);
  return BANK_COLORS[normalizedName] || BANK_COLORS['Default'];
}

/**
 * Generate bank initials for fallback icons
 */
export function getBankInitials(bankName: string): string {
  const normalizedName = normalizeBankName(bankName);

  // Special cases for known abbreviations
  if (normalizedName === 'Banco do Brasil') return 'BB';
  if (normalizedName === 'Caixa') return 'CAIXA';
  if (normalizedName === 'C6 Bank') return 'C6';
  if (normalizedName === 'BTG Pactual') return 'BTG';

  // Generate initials from words
  const words = normalizedName.split(' ');

  if (words.length === 1) {
    // Single word - take first 2-3 letters
    return normalizedName.substring(0, Math.min(3, normalizedName.length)).toUpperCase();
  }

  // Multiple words - take first letter of each word
  const initials = words.map(word => word[0]).join('');
  return initials.substring(0, 3).toUpperCase(); // Max 3 letters
}

/**
 * Check if a bank is a major Brazilian bank
 */
export function isMajorBrazilianBank(bankName: string): boolean {
  const normalizedName = normalizeBankName(bankName);
  const majorBanks = [
    'Banco do Brasil',
    'Bradesco',
    'Itaú',
    'Santander',
    'Caixa',
    'Nubank',
    'Inter',
    'C6 Bank',
    'PicPay',
    'Neon',
    'Original',
    'BTG Pactual'
  ];

  return majorBanks.includes(normalizedName);
}

/**
 * Get logo URL with automatic fallback - updated to use SVG logos
 * @deprecated Use getBankLogoSvgPath() instead for immediate synchronous access to local SVG logos
 * This function will be removed in a future version
 */
export async function getBankLogoUrl(bankIdentifier: string): Promise<{
  url: string;
  isDefault: boolean;
  bankName: string;
}> {
  // Log deprecation warning
  console.warn(
    'getBankLogoUrl() is deprecated. Use getBankLogoSvgPath() instead for immediate access to local SVG logos.'
  );
  // Handle bank codes (like "104") and bank names
  let bankCode = '';
  let normalizedName = '';

  // Check if identifier is a bank code (3-4 digits)
  if (/^\d{3,4}$/.test(bankIdentifier.trim())) {
    bankCode = bankIdentifier.trim().padStart(3, '0');
    normalizedName = getBankNameByCode(bankCode);
  } else {
    // It's a bank name - try to find the corresponding code
    normalizedName = normalizeBankName(bankIdentifier);
    bankCode = ''; // Will be handled by fallback
  }

  // Use the new simplified API internally
  const svgPath = getBankLogoSvgPath(normalizedName);
  const hasLocalLogo = hasLocalBankLogo(normalizedName);

  if (hasLocalLogo && svgPath) {
    // Found local SVG logo
    return {
      url: svgPath,
      isDefault: false,
      bankName: normalizedName
    };
  }

  // Fallback: try local asset path if we have a code
  if (bankCode) {
    const localPath = getBankLogoPathByCode(bankCode);
    if (localPath) {
      return {
        url: localPath,
        isDefault: false,
        bankName: normalizedName
      };
    }
  }

  // Final fallback - create SVG fallback icon
  const fallbackUrl = createBankFallbackIcon(normalizedName);
  return {
    url: fallbackUrl,
    isDefault: true,
    bankName: normalizedName
  };
}

/**
 * Create fallback SVG icon
 */
export function createBankFallbackIcon(bankName: string, size: number = 64): string {
  const color = getBankColor(bankName);
  const initials = getBankInitials(bankName);

  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="30" fill="${color}"/>
      <text x="32" y="32" font-family="Arial, sans-serif" font-size="16" font-weight="bold"
            text-anchor="middle" dominant-baseline="middle" fill="white">
        ${initials}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Format bank name for display
 */
export function formatBankDisplayName(bankName: string): string {
  const normalizedName = normalizeBankName(bankName);

  // Don't abbreviate major banks in display
  if (isMajorBrazilianBank(normalizedName)) {
    return normalizedName;
  }

  // For custom names, return as-is but cleaned
  return bankName.trim();
}

/**
 * Validate bank logo file
 */
export function validateBankLogoFile(file: File): {
  isValid: boolean;
  error?: string;
} {
  // Check file type
  const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Only PNG, JPEG, and SVG files are allowed'
    };
  }

  // Check file size (1MB max)
  const maxSize = 1024 * 1024; // 1MB
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size must be less than 1MB'
    };
  }

  return { isValid: true };
}

/**
 * Search and match banks with fuzzy logic
 */
export function searchBanks(bankName: string, availableBanks: BankLogo[]): BankLogo[] {
  if (!bankName.trim()) return [];

  const searchTerm = normalizeBankName(bankName.toLowerCase());

  // Exact matches first
  const exactMatches = availableBanks.filter(bank =>
    normalizeBankName(bank.bank_name.toLowerCase()) === searchTerm
  );

  if (exactMatches.length > 0) {
    return exactMatches;
  }

  // Partial matches
  const partialMatches = availableBanks.filter(bank =>
    normalizeBankName(bank.bank_name.toLowerCase()).includes(searchTerm) ||
    searchTerm.includes(normalizeBankName(bank.bank_name.toLowerCase()))
  );

  // Sort by relevance (shorter matches first)
  return partialMatches.sort((a, b) => {
    const aName = normalizeBankName(a.bank_name.toLowerCase());
    const bName = normalizeBankName(b.bank_name.toLowerCase());

    const aScore = Math.abs(aName.length - searchTerm.length);
    const bScore = Math.abs(bName.length - searchTerm.length);

    return aScore - bScore;
  });
}

/**
 * Cache management for logo URLs
 */
class LogoUrlCache {
  private cache = new Map<string, { url: string; timestamp: number }>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes

  get(bankName: string): string | null {
    const cached = this.cache.get(bankName);
    if (cached && Date.now() - cached.timestamp < this.TTL) {
      return cached.url;
    }
    return null;
  }

  set(bankName: string, url: string): void {
    this.cache.set(bankName, {
      url,
      timestamp: Date.now()
    });
  }

  clear(): void {
    this.cache.clear();
  }

  // Clean expired entries
  cleanup(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp >= this.TTL) {
        this.cache.delete(key);
      }
    }
  }
}

export const logoUrlCache = new LogoUrlCache();

// Cleanup cache every 10 minutes
setInterval(() => logoUrlCache.cleanup(), 10 * 60 * 1000);

/**
 * Get cached logo URL
 * @deprecated Use getBankLogoSvgPath() instead for immediate synchronous access
 * This function will be removed in a future version
 */
export async function getCachedBankLogoUrl(bankName: string): Promise<string> {
  // Log deprecation warning
  console.warn(
    'getCachedBankLogoUrl() is deprecated. Use getBankLogoSvgPath() instead for immediate synchronous access.'
  );

  // Use the new simplified API - no caching needed for static files
  const result = await getBankLogoUrl(bankName);
  return result.url;
}